import { compact, head, map, keyBy } from 'lodash';
import { camelCase } from 'lib/utils/objectSerializer';

import {
  OFFER_FULFILLMENT_METHODS,
  PAGINATION_DEFAULT,
  PAGINATION_PER_PAGE,
} from 'lib/pages/marketer/values';
import { RestaurantBranch } from 'lib/types/global';
import { OfferAction } from './actions';
import {
  API_DELETE_OFFER_FAILURE,
  API_READ_ACTIVE_OFFER_CAMPAIGNS_SUCCESS,
  API_READ_OFFER_CAMPAIGN_ORDERS_FAILURE,
  API_READ_OFFER_CAMPAIGN_ORDERS_REQUEST,
  API_READ_OFFER_CAMPAIGN_ORDERS_SUCCESS,
  API_READ_OFFER_CAMPAIGN_REDEMPTIONS_FAILURE,
  API_READ_OFFER_CAMPAIGN_REDEMPTIONS_REQUEST,
  API_READ_OFFER_CAMPAIGN_REDEMPTIONS_SUCCESS,
  API_READ_EXISTING_OFFER_SUCCESS,
  API_READ_OFFER_MENU_ITEMS_SUCCESS,
  API_READ_OFFER_CONFIG_SUCCESS,
  API_READ_OFFER_CONFIG_FAILURE,
  API_READ_OFFERS_SUCCESS,
  API_READ_OFFERS_FAILURE,
  API_READ_PAGINATED_OFFERS_SUCCESS,
  API_READ_PAGINATED_OFFERS_FAILURE,
  API_SAVE_OFFER_SUCCESS,
  API_SAVE_OFFER_FAILURE,
  CLEAR_EXISTING_OFFER,
} from './actionTypes';
import {
  Config,
  ItemOfferConfig,
  Offer,
  OffersState,
  OfferKind,
  Order,
  Item,
  Category,
} from './types';

const OFFER_TIMEZONE = 'Asia/Dubai'; // TODO: Initialise timezone for Pilot

type ExistingOffersState = Omit<OffersState, 'newOffer'>;

export const initialState: ExistingOffersState = {
  campaign: undefined,
  campaignRedemption: undefined,
  campaignOrders: [],
  existingOffers: [],
  existingOffersById: {},
  paginatedOffers: {
    metadata: {
      pagination: {
        page: PAGINATION_DEFAULT,
        resultsPerPage: PAGINATION_PER_PAGE,
      },
    },
    offers: [],
  },
  timezone: OFFER_TIMEZONE,
  inventory: { categories: [], items: [] },
};

// mapOffer returns the domain representation of an offer given an
// API-received offer.
function mapOffer(apiOffer: Record<string, any>): Offer {
  const offer: Offer = camelCase(apiOffer);

  const { startAt } = offer;

  if (startAt) {
    offer.startDate = startAt;
  }

  if (offer.fulfillmentMethod) {
    offer.fulfillmentMethods = [offer.fulfillmentMethod];
  } else {
    offer.fulfillmentMethods = [...OFFER_FULFILLMENT_METHODS];
  }

  return offer;
}

function mapOffers(apiOffers: Record<string, any>[]): Offer[] {
  const offers = (apiOffers || []).map((apiOffer) => mapOffer(apiOffer));
  return compact(offers);
}

function mapCampaignOrders(response: Record<string, any>[]): Order[] {
  const orders = (response || []).map((v) => ({
    id: v.order_number,
    subtotal: v.amount.formatted,
    placedAt: v.timeline.placed_at,
  }));
  return compact(orders);
}

export default function offersReducer(
  state: ExistingOffersState = initialState,
  action: OfferAction,
): ExistingOffersState {
  switch (action.type) {
    case API_READ_ACTIVE_OFFER_CAMPAIGNS_SUCCESS: {
      const campaign = camelCase(head(action.payload) || {});
      if (!campaign || campaign.name !== 'Eat Out to Help Out') {
        return state;
      }

      return {
        ...state,
        campaign,
      };
    }

    case API_READ_OFFER_CAMPAIGN_REDEMPTIONS_REQUEST: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case API_READ_OFFER_CAMPAIGN_REDEMPTIONS_SUCCESS: {
      const payload = camelCase(action.payload || {});
      return {
        ...state,
        campaignRedemption: {
          totalOrders: payload.totalOrders,
          totalDiscount: payload.totalDiscount.formatted,
        },
        isLoading: false,
      };
    }

    case API_READ_OFFER_CAMPAIGN_REDEMPTIONS_FAILURE: {
      const { status } = action;
      return {
        ...state,
        campaignRedemption: undefined,
        campaignError: {
          status,
        },
        isLoading: false,
      };
    }

    case API_READ_OFFER_CAMPAIGN_ORDERS_REQUEST: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case API_READ_OFFER_CAMPAIGN_ORDERS_SUCCESS: {
      const payload = camelCase(action.payload);
      return {
        ...state,
        campaignOrders: mapCampaignOrders((payload || {}).orders),
        isLoading: false,
      };
    }

    case API_READ_OFFER_CAMPAIGN_ORDERS_FAILURE: {
      const { status } = action;
      return {
        ...state,
        campaignOrders: [],
        campaignError: {
          status,
        },
        isLoading: false,
      };
    }

    case API_READ_EXISTING_OFFER_SUCCESS: {
      const offer = mapOffer(action.payload);
      const existingOffersById = {
        ...state.existingOffersById,
        [offer.id]: offer,
      };
      return {
        ...state,
        existingOffers: map(existingOffersById, 'id'),
        existingOffersById,
      };
    }

    case API_READ_OFFERS_SUCCESS: {
      const offers = mapOffers(action.payload.offers);

      return {
        ...state,
        existingOffers: map(offers, 'id'),
        existingOffersById: keyBy(offers, 'id'),
      };
    }

    case API_READ_OFFERS_FAILURE: {
      const { status } = action;
      return {
        ...state,
        error: {
          status,
        },
      };
    }

    case API_READ_PAGINATED_OFFERS_SUCCESS: {
      if (!action.payload) {
        return {
          ...state,
          paginatedOffers: {
            metadata: {
              pagination: {
                hasNextPage: false,
                page: 1,
                resultsPerPage: 0,
              },
              restaurantIds: [],
              totalCount: 0,
            },
            offers: [],
          },
        };
      }

      const { metadata, offers } = action.payload;
      const mappedOffers = mapOffers(offers);
      return {
        ...state,
        paginatedOffers: {
          metadata: camelCase(metadata),
          offers: mappedOffers,
        },
      };
    }

    case API_READ_PAGINATED_OFFERS_FAILURE: {
      const { status } = action;
      return {
        ...state,
        error: {
          status,
        },
      };
    }

    case API_SAVE_OFFER_SUCCESS: {
      const offers = mapOffers([action.payload]);
      return {
        ...state,
        existingOffers: map(offers, 'id'),
        existingOffersById: keyBy(offers, 'id'),
      };
    }

    case API_SAVE_OFFER_FAILURE: {
      const { status } = action;

      return {
        ...state,
        error: {
          status,
        },
      };
    }

    case CLEAR_EXISTING_OFFER: {
      // Remove the specified offer by ID.
      const { offerId } = action;
      const offers = (state.existingOffers || []).filter(
        (id) => id !== offerId,
      );
      const offersById = state.existingOffersById;
      delete offersById[offerId];

      return {
        ...state,
        existingOffers: offers,
        existingOffersById: offersById,
      };
    }

    case API_DELETE_OFFER_FAILURE: {
      const { status } = action;

      return {
        ...state,
        error: {
          status,
        },
      };
    }

    case API_READ_OFFER_MENU_ITEMS_SUCCESS: {
      const inventory: { categories: Category[]; items: Item[] } = camelCase(
        action.payload,
      );
      const branches: RestaurantBranch[] = action.extra.branches;

      const { categories, items } = inventory;

      const itemCategories = categories.reduce<{
        [itemPk: string]: Category[];
      }>(
        (acc, category) => ({
          ...acc,
          ...category.itemPks.reduce<{ [itemPk: string]: Category[] }>(
            (acc, itemPk) => ({
              ...acc,
              [itemPk]: acc[itemPk] ? [...acc[itemPk], category] : [category],
            }),
            {},
          ),
        }),
        {},
      );

      const branchesById = branches.reduce<{
        [branchDrnId: string]: RestaurantBranch;
      }>((acc, branch) => ({ ...acc, [branch.drnId]: branch }), {});

      const itemBranches = items.reduce<{
        [itemPk: string]: RestaurantBranch[];
      }>(
        (acc, item) => ({
          ...acc,
          [item.itemPk]: item.branchDrnIds.map(
            (branchDrnId) => branchesById[branchDrnId],
          ),
        }),
        {},
      );

      return {
        ...state,
        inventory: {
          categories,
          items: items.map((item) => ({
            ...item,
            categories: itemCategories[item.itemPk],
            branches: itemBranches[item.itemPk],
          })),
        },
      };
    }

    case API_READ_OFFER_CONFIG_SUCCESS: {
      const config: Config = camelCase(action.payload);
      if (!config) {
        return state;
      }
      const itemsConfig = config.offerTypes.find(
        (offerType): offerType is ItemOfferConfig =>
          offerType.type === OfferKind.ITEM,
      )!;
      const lowestItemPrice = itemsConfig.themes
        .map(({ minimumItemPrice }) => minimumItemPrice)
        .reduce((min, nextNumber) => Math.min(min, nextNumber));
      itemsConfig.lowestItemPrice = lowestItemPrice;
      const maximumItemCount = itemsConfig.themes
        .map(({ maximumItemCount }) => maximumItemCount)
        .reduce((prev, curr) => curr || prev);
      itemsConfig.maximumItemCount = maximumItemCount;

      return {
        ...state,
        ...config,
        fulfillmentMethods: config.fulfillmentMethods || [],
      };
    }

    case API_READ_OFFER_CONFIG_FAILURE: {
      const { status } = action;

      return {
        ...state,
        currency: '',
        fulfillmentMethods: [],
        error: {
          status,
        },
        isLoading: false,
      };
    }

    default:
      return state;
  }
}
